/**
 * Scoring Validator Utility
 * Implements 10 validation checks for scoring integrity
 * All validations return arrays of flag objects that can be persisted to the database
 */

// Weight bounds for validation
const WEIGHT_MIN = 0.5
const WEIGHT_MAX = 2.5

// Score variance threshold for regression detection
const SCORE_VARIANCE_THRESHOLD = 20 // percentage points

/**
 * Run all validation checks against calculated scores
 * @param {Object} scores - Calculated scores from calculateScores()
 * @param {Object} responses - Assessment responses
 * @param {Object} previousScores - Previous scores for regression detection (optional)
 * @returns {Array} - Array of validation flag objects
 */
export function runValidationChecks(scores, responses, previousScores = null) {
  const flags = []

  // Check 1: Weight bounds
  flags.push(...checkWeightBounds(scores))

  // Check 2: Domain score range
  flags.push(...checkDomainScoreRange(scores))

  // Check 3: Completeness
  flags.push(...checkCompleteness(scores))

  // Check 4: Context factor resolution
  flags.push(...checkContextFactorResolution(scores))

  // Check 5: Alignment consistency
  flags.push(...checkAlignmentConsistency(scores))

  // Check 6: TAPS range (if available)
  if (scores.threatAdjusted) {
    flags.push(...checkTAPSRange(scores.threatAdjusted))
  }

  // Check 7: Loss scenario reasonability (if available)
  if (scores.lossScenarios && responses.impactData) {
    flags.push(...checkLossReasonability(scores.lossScenarios, responses.impactData))
  }

  // Check 8: Duplicate context factor matches
  flags.push(...checkDuplicateContextMatches(scores))

  // Check 9: Score regression detection
  if (previousScores) {
    flags.push(...checkScoreRegression(scores, previousScores))
  }

  // Check 10: Empty domain on completed assessment
  if (responses.assessmentStatus === 'completed') {
    flags.push(...checkEmptyCompletedDomains(scores))
  }

  // Check 11: Industry weight bounds
  if (scores.riskAppetite?.domainWeightDetails) {
    flags.push(...checkIndustryWeightBounds(scores.riskAppetite))
  }

  return flags
}

/**
 * Check 1: Weight bounds - combinedWeight should be 0.5-2.5
 */
function checkWeightBounds(scores) {
  const flags = []

  if (scores.maturity?.categoryDetails) {
    Object.entries(scores.maturity.categoryDetails).forEach(([domainId, questions]) => {
      if (Array.isArray(questions)) {
        questions.forEach(q => {
          if (q.combinedWeight > WEIGHT_MAX) {
            flags.push({
              checkName: 'weight_upper_bound',
              severity: 'warning',
              message: `Question ${q.id} has combined weight of ${q.combinedWeight.toFixed(2)} (exceeds ${WEIGHT_MAX} cap).`,
              details: {
                questionId: q.id,
                domainId,
                combinedWeight: q.combinedWeight,
                baseWeight: q.baseWeight,
                contextWeight: q.contextWeight
              }
            })
          }
          if (q.combinedWeight < WEIGHT_MIN && q.combinedWeight > 0) {
            flags.push({
              checkName: 'weight_lower_bound',
              severity: 'info',
              message: `Question ${q.id} has very low combined weight of ${q.combinedWeight.toFixed(2)}.`,
              details: { questionId: q.id, domainId, combinedWeight: q.combinedWeight }
            })
          }
        })
      }
    })
  }

  return flags
}

/**
 * Check 2: Domain score range - must be 0-100
 */
function checkDomainScoreRange(scores) {
  const flags = []

  if (scores.maturity?.categoryScores) {
    Object.entries(scores.maturity.categoryScores).forEach(([domainId, domain]) => {
      if (domain.score < 0 || domain.score > 100) {
        flags.push({
          checkName: 'domain_score_range',
          severity: 'error',
          message: `Domain "${domain.name}" has out-of-range score: ${domain.score}%. Must be 0-100.`,
          details: { domainId, score: domain.score, name: domain.name }
        })
      }
    })
  }

  return flags
}

/**
 * Check 3: Completeness - flag domains with unanswered questions
 */
function checkCompleteness(scores) {
  const flags = []

  if (scores.maturity?.categoryScores) {
    Object.entries(scores.maturity.categoryScores).forEach(([domainId, domain]) => {
      if (domain.answeredCount < domain.questionCount) {
        const unanswered = domain.questionCount - domain.answeredCount
        flags.push({
          checkName: 'incomplete_domain',
          severity: 'warning',
          message: `Domain "${domain.name}" has ${domain.answeredCount} of ${domain.questionCount} questions answered. ${unanswered} unanswered questions scored as 0.`,
          details: {
            domainId,
            name: domain.name,
            answered: domain.answeredCount,
            total: domain.questionCount,
            unanswered
          }
        })
      }
    })
  }

  return flags
}

/**
 * Check 4: Context factor resolution - flag questions where context factors had no matches
 */
function checkContextFactorResolution(scores) {
  const flags = []

  if (scores.maturity?.categoryDetails) {
    Object.entries(scores.maturity.categoryDetails).forEach(([domainId, questions]) => {
      if (Array.isArray(questions)) {
        questions.forEach(q => {
          // If contextQuestions defined but weight resolved to neutral, may indicate no matches
          if (q.contextWeight === 1.0 && q.contextQuestions && q.contextQuestions.trim() !== '') {
            flags.push({
              checkName: 'unresolved_context_factors',
              severity: 'info',
              message: `Question ${q.id}: has contextQuestions defined but context weight is 1.0 (neutral). Context factors may not have matched any scoping responses.`,
              details: {
                questionId: q.id,
                domainId,
                contextQuestions: q.contextQuestions,
                contextWeight: q.contextWeight
              }
            })
          }
        })
      }
    })
  }

  return flags
}

/**
 * Check 5: Alignment consistency - very risk averse but very high maturity is unusual
 */
function checkAlignmentConsistency(scores) {
  const flags = []

  const appetite = scores.riskAppetite?.averageAppetite
  const maturity = scores.overall?.score

  if (appetite && maturity) {
    // Very risk-averse (low appetite) with high maturity
    if (appetite <= 1.5 && maturity > 80) {
      flags.push({
        checkName: 'alignment_consistency',
        severity: 'info',
        message: `Organization is very risk-averse (appetite: ${appetite.toFixed(1)}) but has high maturity (${maturity}%). Verify risk appetite inputs are correct.`,
        details: { appetite, maturity }
      })
    }

    // High risk tolerance with very low maturity
    if (appetite >= 4.0 && maturity < 30) {
      flags.push({
        checkName: 'alignment_consistency',
        severity: 'warning',
        message: `Organization has high risk tolerance (appetite: ${appetite.toFixed(1)}) but very low maturity (${maturity}%). This may indicate unaware risk-taking.`,
        details: { appetite, maturity }
      })
    }
  }

  return flags
}

/**
 * Check 6: TAPS range - should be 0-100
 */
function checkTAPSRange(threatAdjustedScores) {
  const flags = []

  if (threatAdjustedScores?.adjustedDomains) {
    Object.entries(threatAdjustedScores.adjustedDomains).forEach(([domainId, domain]) => {
      const taps = domain.threatAdjustedPriorityScore
      if (taps < 0 || taps > 100) {
        flags.push({
          checkName: 'taps_range',
          severity: 'error',
          message: `Domain "${domain.name}" has out-of-range TAPS: ${taps}. Must be 0-100.`,
          details: { domainId, taps, name: domain.name }
        })
      }
    })
  }

  return flags
}

/**
 * Check 7: Loss scenario reasonability - should not exceed 10x annual revenue
 */
function checkLossReasonability(lossScenarios, impactData) {
  const flags = []

  // Revenue band to max value mapping (supports both new and legacy labels)
  const revenueCeilings = {
    'Under $25M': 25000000,
    '$25M \u2013 $50M': 50000000,
    '$50M \u2013 $100M': 100000000,
    '$100M \u2013 $250M': 250000000,
    '$250M \u2013 $500M': 500000000,
    'Over $500M': 1000000000,
    // Legacy mappings
    '<$5mm': 5000000,
    '$5mm - $25mm': 25000000,
    '$25mm - $100mm': 100000000,
    '>$100mm': 500000000
  }

  const revenueValue = impactData.estimated_annual_revenue
  const maxRevenue = revenueCeilings[revenueValue] || (revenueValue ? parseFloat(revenueValue) : 25000000)

  if (Array.isArray(lossScenarios)) {
    lossScenarios.forEach(scenario => {
      if (scenario.lossRange?.high > maxRevenue * 10) {
        flags.push({
          checkName: 'loss_reasonability',
          severity: 'warning',
          message: `Loss scenario "${scenario.threatName}" high estimate ($${(scenario.lossRange.high / 1000000).toFixed(1)}M) exceeds 10x annual revenue.`,
          details: {
            threatName: scenario.threatName,
            highLoss: scenario.lossRange.high,
            revenueMax: maxRevenue
          }
        })
      }
    })
  }

  return flags
}

/**
 * Check 8: Duplicate context factor matches - detect ambiguous keyword collisions
 */
function checkDuplicateContextMatches(scores) {
  const flags = []

  // This check requires access to the CONTEXT_FACTOR_MAP which is in scoring.js
  // For now, we'll flag this at the scoring layer level if detected there
  // This is a placeholder for when we have access to the matched context info

  if (scores._contextMatchInfo?.duplicateMatches) {
    scores._contextMatchInfo.duplicateMatches.forEach(match => {
      flags.push({
        checkName: 'duplicate_context_match',
        severity: 'warning',
        message: `Context factor "${match.factor}" matches ${match.matchCount} CONTEXT_FACTOR_MAP entries. Longest key used: "${match.selectedKey}".`,
        details: {
          factor: match.factor,
          matches: match.allMatches,
          selectedKey: match.selectedKey
        }
      })
    })
  }

  return flags
}

/**
 * Check 9: Score regression detection - significant score changes
 */
function checkScoreRegression(scores, previousScores) {
  const flags = []

  if (previousScores?.maturity?.categoryScores && scores.maturity?.categoryScores) {
    Object.entries(scores.maturity.categoryScores).forEach(([domainId, domain]) => {
      const prevDomain = previousScores.maturity.categoryScores[domainId]
      if (prevDomain) {
        const delta = Math.abs(domain.score - prevDomain.score)
        if (delta > SCORE_VARIANCE_THRESHOLD) {
          flags.push({
            checkName: 'score_regression',
            severity: 'warning',
            message: `Domain "${domain.name}" score changed by ${delta} points (${prevDomain.score}% → ${domain.score}%).`,
            details: {
              domainId,
              name: domain.name,
              previousScore: prevDomain.score,
              currentScore: domain.score,
              delta
            }
          })
        }
      }
    })
  }

  return flags
}

/**
 * Check 10: Empty domain on completed assessment
 */
function checkEmptyCompletedDomains(scores) {
  const flags = []

  if (scores.maturity?.categoryScores) {
    Object.entries(scores.maturity.categoryScores).forEach(([domainId, domain]) => {
      if (domain.answeredCount === 0) {
        flags.push({
          checkName: 'empty_completed_domain',
          severity: 'error',
          message: `Assessment is marked complete but domain "${domain.name}" has 0 answered questions.`,
          details: {
            domainId,
            name: domain.name,
            questionCount: domain.questionCount
          }
        })
      }
    })
  }

  return flags
}

/**
 * Check 11: Industry weight bounds
 */
function checkIndustryWeightBounds(riskAppetiteScores) {
  const flags = []
  const MIN_WEIGHT = 0.5
  const MAX_WEIGHT = 2.0

  if (riskAppetiteScores.domainWeightDetails) {
    riskAppetiteScores.domainWeightDetails.forEach(detail => {
      if (detail.industryWeight < MIN_WEIGHT || detail.industryWeight > MAX_WEIGHT) {
        flags.push({
          checkName: 'industry_weight_bounds',
          severity: 'warning',
          message: `Industry weight for "${detail.domain}" is ${detail.industryWeight} (outside ${MIN_WEIGHT}-${MAX_WEIGHT} range).`,
          details: {
            domain: detail.domain,
            weight: detail.industryWeight,
            industry: riskAppetiteScores.industry
          }
        })
      }
    })
  }

  return flags
}

/**
 * Build the complete scoring event data object for the audit log
 * @param {Object} scores - Calculated scores
 * @param {Object} responses - Assessment responses
 * @param {Object} engagementContext - Engagement driver context (optional)
 * @returns {Object} - Event data for audit logging
 */
export function buildScoringEventData(scores, responses, engagementContext = null) {
  // Extract engagement driver responses (ed-* questions)
  const businessScopingResponses = responses.businessScoping || {}
  const engagementDriverResponses = {}
  Object.entries(businessScopingResponses).forEach(([key, value]) => {
    if (key.startsWith('ed-')) {
      engagementDriverResponses[key] = typeof value === 'object' ? value.value : value
    }
  })

  return {
    timestamp: new Date().toISOString(),
    inputs: {
      businessScopingResponseCount: Object.keys(responses.businessScoping || {}).filter(k => !k.startsWith('ed-')).length,
      technologyScopingResponseCount: Object.keys(responses.technologyScoping || {}).length,
      riskAppetiteResponseCount: Object.keys(responses.riskAppetite || {}).length,
      maturityResponseCount: Object.keys(responses.maturityAssessment || {}).length,
      engagementDriverResponseCount: Object.keys(engagementDriverResponses).length
    },
    engagementDriver: {
      hasData: Object.keys(engagementDriverResponses).length > 0,
      responses: engagementDriverResponses,
      primaryDrivers: engagementContext?.primaryDriver?.values || [],
      primaryDriver: engagementContext?.primaryDriver?.value || null,
      timeline: engagementContext?.timeline?.value || null,
      desiredOutcomes: engagementContext?.desiredOutcome?.values || [],
      desiredOutcome: engagementContext?.desiredOutcome?.value || null,
      urgencyMultiplier: engagementContext?.urgencyMultiplier || 1.0,
      narrativeHook: engagementContext?.narrativeHook || null,
      tags: engagementContext?.tags || []
    },
    maturityScoring: {
      overallScore: scores.overall?.score || 0,
      overallMaturityLevel: scores.overall?.maturityLevel || null,
      categoryCount: Object.keys(scores.maturity?.categoryScores || {}).length,
      categoryScoreSummary: Object.fromEntries(
        Object.entries(scores.maturity?.categoryScores || {}).map(([id, d]) => [id, {
          score: d.score,
          level: d.maturityLevel?.level
        }])
      )
    },
    contextWeighting: {
      businessScopingOverallWeight: scores.businessScoping?.overallContextWeight || 1.0,
      technologyScopingOverallWeight: scores.technologyScoping?.overallContextWeight || 1.0,
      perDomainAverageContextWeights: Object.fromEntries(
        Object.entries(scores.maturity?.categoryScores || {}).map(([id, d]) => [id, d.averageContextWeight || 1.0])
      )
    },
    riskAppetite: {
      averageAppetite: scores.riskAppetite?.averageAppetite || 0,
      simpleAverage: scores.riskAppetite?.simpleAverage || 0,
      domainCount: scores.riskAppetite?.domainCount || Object.keys(scores.riskAppetite?.domains || {}).length,
      industryWeightsApplied: scores.riskAppetite?.industryWeightsApplied || false,
      industry: scores.riskAppetite?.industry || null,
      domainWeightDetails: scores.riskAppetite?.domainWeightDetails || [],
      backwardCompatMerges: scores.riskAppetite?.backwardCompatMerges || [],
      perDomainScores: Object.fromEntries(
        Object.entries(scores.riskAppetite?.domains || {}).map(([d, data]) => [d, data.score])
      )
    },
    budgetContext: {
      itBudgetPct: responses.businessScoping?.['bs-41']?.label || null,
      itBudgetWeight: responses.businessScoping?.['bs-41']?.weight || null,
      securityBudgetPct: responses.businessScoping?.['bs-42']?.label || null,
      securityBudgetWeight: responses.businessScoping?.['bs-42']?.weight || null
    }
  }
}

/**
 * Get a summary of validation results
 * @param {Array} flags - Array of validation flags
 * @returns {Object} - Summary object
 */
export function getValidationSummary(flags) {
  return {
    total: flags.length,
    byCheckName: flags.reduce((acc, f) => {
      acc[f.checkName] = (acc[f.checkName] || 0) + 1
      return acc
    }, {}),
    bySeverity: {
      info: flags.filter(f => f.severity === 'info').length,
      warning: flags.filter(f => f.severity === 'warning').length,
      error: flags.filter(f => f.severity === 'error').length
    },
    hasErrors: flags.some(f => f.severity === 'error'),
    hasWarnings: flags.some(f => f.severity === 'warning')
  }
}
